import type { GroupMemberRef } from "../member-resolver.js";
import { AI_CAPABILITY_REGISTRY } from "../registry/registry.js";
import type { AiCapabilityPage, AiSlot, AiSlotType } from "../registry/schema.js";
import { resolveDateSlot } from "./date-slot.js";
import { resolveEnumSlot } from "./enum-slot.js";
import { memberTextIsGroupPhrase } from "./member-reference.js";
import { resolveMemberSlot } from "./member-slot.js";
import { resolveMoneySlot } from "./money-slot.js";
import { resolveNamedRefSlot, type NamedRef } from "./named-ref-slot.js";
import { SLOT_RESOLUTION_REASON, slotNone, slotResolved, type SlotResolution, type SlotResolutionReason, type SlotValue } from "./slot-resolution.js";
import { resolveNameText } from "./name-text.js";
import { resolveListSlot, resolveNoteText, resolveTextSlot } from "./text-slot.js";

/**
 * 🔴 US-AIV2-0022 — BỘ PHÂN PHỐI theo `slot.type`: MỘT cửa cho Người làm (0023) đổi chữ → giá trị.
 *
 * Tập `AI_SLOT_TYPES` (schema Sổ) là tập ĐÓNG và `switch` dưới đây là VÉT CẠN (`never` ở `default`): thêm loại ô vào Sổ
 * mà chưa có resolver ⇒ typecheck đỏ, và `slot-type-coverage.invariants.spec.ts` duyệt cả tập ở runtime. Không có
 * "resolver mặc định trả chữ nguyên văn" cho loại lạ — đó là cách một ô tiền lọt qua thành chuỗi.
 *
 * Phạm vi (`SlotScope`) do tầng api NẠP THEO ACTOR rồi truyền vào; domain không tra DB, không đọc đồng hồ (`now` bắt buộc).
 */
export interface SlotScope {
  /** Mốc "bây giờ" — caller truyền, hàm thuần. */
  readonly now: Date;
  /** IANA; vắng ⇒ `DEFAULT_GROUP_TIMEZONE`. */
  readonly timeZone?: string;
  /** Thành viên ACTIVE của nhóm hiệu lực — vắng khi phiên chưa có nhóm. */
  readonly members?: readonly GroupMemberRef[];
  /** Quỹ actor thấy được (đã lọc theo nhóm hiệu lực nếu có). */
  readonly funds?: readonly NamedRef[];
  /** Đợt thu đang mở trong các quỹ của phạm vi. */
  readonly collections?: readonly NamedRef[];
  /** Nhóm actor có ghế ACTIVE. */
  readonly groups?: readonly NamedRef[];
  /**
   * 🔴 US-AIV2-0023d (@UC0226) — VÌ SAO danh sách trên rỗng, khi loader đo được (mọi quỹ trong tầm nhìn đang
   * ĐÓNG BĂNG ⇒ `scope_fund_frozen`). Chỉ có nghĩa khi danh sách tương ứng rỗng; vắng ⇒ `empty_scope` chung.
   * Đo ở tầng api (nơi thấy `funds.status`), nói ở tầng Sổ — domain thuần chỉ chuyển tiếp, không tự suy.
   */
  readonly emptyReason?: SlotResolutionReason;
  /**
   * 🔴 US-AIV2-0110 S27b (@UC0279 · QĐ PO 27/09) — bậc của trang đang hỏi ô. Chỉ `read` nới luật S11 cho ô QUỸ (xem
   * `soleRefWhenUnmatched`). Vắng ⇒ coi như trang GHI (fail-closed: không nới).
   */
  readonly pageTier?: AiCapabilityPage["tier"];
  /**
   * 🔴 US-AIV2-0111 C10 (@UC0205 @UC0206 @UC0207) — trang được mở TỪ CÂU người dùng gõ (Người phiên dịch chọn trang), không phải
   * từ một NÚT (`run_page` của menu/thẻ lời chào). Chỉ quyết một việc: ô enum TRỐNG ở trang đọc có dùng `fallback` không.
   *  · mở bằng câu mà câu không nói chỉ số nào (*"tháng này em phải đóng bao nhiêu"* — LLM không gạch cụm nào) ⇒ mặc định, không hỏi;
   *  · mở bằng NÚT ⇒ vẫn hỏi: nút "Xem ai giữ vai gì" (thẻ `BLOCKED-ASK-MANAGER`) mở READ-PEOPLE-MINE với ô trống, và mặc định ở
   *    đó ("khoản của tôi") sẽ trả lời SAI câu hỏi của chính cái nút; nút menu "Xem tiền của quỹ" giữ lối chọn chỉ số bằng nút.
   * Vắng ⇒ coi như mở bằng nút (fail-closed: hỏi như trước bản vá). Ô CÓ chữ mà chữ không khớp gì thì không cần cờ này — người
   * dùng đã nói về chỉ số, chỉ là nói bằng chữ Sổ chưa biết.
   */
  readonly openedByText?: boolean;
  /**
   * 🔴 US-AIV2-0111 C07g (@UC0203) — ghế của CHÍNH người đang gõ trong nhóm hiệu lực (tầng api nạp cùng lượt với `members`). Bộ phân
   * phối CHỈ chuyền nó cho ô `memberScope: "payer"` ("mình ứng" = người gõ); ô khác không bao giờ thấy nó ⇒ "mình" ở ô nghĩa vụ vẫn hỏi.
   */
  readonly selfMemberId?: string;
  /**
   * 🔴 AI-REDUNDANT-ASK (@UC0162) — CẢ câu người gõ ở lượt này (vắng = nút / không có câu). Bộ phân phối CHỈ chuyền nó cho ô `money`: "1tr2" đọc thẳng
   * trừ khi câu có danh từ đếm ngay sau span ("1tr2 người") — luật ở `resolveMoneySlot`.
   */
  readonly sentence?: string | null;
}

/**
 * 🔴 AI-INTERP N6 (@UC0161) — các ô `date` mà Sổ KHAI là HẠN của đợt thu (`collectionFromSentence.due`, hôm nay: COL-DEADLINE.hanChot). Nhận theo
 * ĐỐI TƯỢNG ô của Sổ (Người làm chuyền đúng ô của trang — `page.slots`), không theo id/tên: một ô khác tên "hanChot" ở trang khác không được nới.
 */
const DUE_DATE_SLOTS: ReadonlySet<AiSlot> = new Set(
  AI_CAPABILITY_REGISTRY.flatMap((p) => {
    const due = p.collectionFromSentence?.due;
    return due === undefined ? [] : p.slots.filter((s) => s.id === due && s.type === "date");
  }),
);

export function resolveSlot(
  slot: AiSlot,
  text: string | null | undefined,
  scope: SlotScope,
): SlotResolution<SlotValue> {
  const type: AiSlotType = slot.type;
  switch (type) {
    case "member":
      // C07g: đại từ ngôi 1 chỉ ở ô người ỨNG — luật theo phạm vi ô của Sổ, không theo trang (một ô, một nghĩa).
      // AI-INTERP C3 review MEDIUM: ô TUỲ CHỌN mà chữ không khớp ai VÀ không nêu ai ⇒ như chưa nói (`optionalMemberUnsaid`).
      return optionalMemberUnsaid(
        slot,
        text,
        resolveMemberSlot(text, scope.members, slot.memberScope === "payer" && scope.selfMemberId !== undefined ? { selfMemberId: scope.selfMemberId } : {}),
      );
    case "money":
      return resolveMoneySlot(text, { sentence: scope.sentence ?? null });
    case "fund":
      // S27b (@UC0279 · QĐ PO 27/09): trang ĐỌC ⇒ chữ không khớp + đúng 1 quỹ ⇒ dùng quỹ đó; trang GHI giữ chốt S11.
      return soleRefWhenUnmatched(slot, resolveNamedRefSlot(text, scope.funds, scope.emptyReason, slot), scope.funds, scope.pageTier === "read");
    case "collection":
      return resolveNamedRefSlot(text, scope.collections, scope.emptyReason, slot);
    case "group":
      // 🔴 review #860 MED-2 — ô `group` KHÔNG nhận `emptyReason`. Trường đó chỉ được đặt bởi hai loader QUỸ và ĐỢT THU
      // (`slot-resolver.service.ts`: nhánh `fund` và `collection`), tức nó mang **lý do của quỹ**. Chuyền nó sang nhánh
      // `group` là để một ô đi mượn ngữ nghĩa của ô khác: hôm nay vô hại vì `loadGroups` không bao giờ đặt trường này,
      // nhưng ngày ai đó đặt `emptyReason` cho một mục đích khác thì ô nhóm sẽ nói **lý do của quỹ** mà không ai đỏ.
      // Bỏ tham số thứ ba ⇒ nhóm rỗng rơi về `EMPTY_SCOPE` đúng nghĩa của chính nó.
      return soleRefWhenUnmatched(slot, resolveNamedRefSlot(text, scope.groups, undefined, slot), scope.groups, true);
    case "date":
      // US-AIV2-0114 L07 (@UC0219): ô đầu khoảng (Sổ khai `rangeEdge`) đọc thêm cụm KHOẢNG — luật theo ô của Sổ, không theo trang.
      // AI-INTERP N6 (@UC0161): ô Sổ khai là HẠN (`collectionFromSentence.due`) ⇒ "dd/mm" đã qua là năm sau.
      return resolveDateSlot(text, {
        now: scope.now,
        timeZone: scope.timeZone,
        ...(slot.rangeEdge === undefined ? {} : { rangeEdge: slot.rangeEdge }),
        ...(DUE_DATE_SLOTS.has(slot) ? { horizon: "future" as const } : {}),
      });
    case "enum":
      // US-AIV2-0111 C10 (@UC0205 @UC0206 @UC0207): mặc định của ô enum CHỈ ở trang ĐỌC — cùng luật bậc với S27b ở trên (vắng ⇒ không nới).
      return resolveEnumSlot(text, slot, { allowFallback: scope.pageTier === "read" && ((text ?? "").trim() !== "" || scope.openedByText === true) });
    case "text":
      // US-AIV2-0113 M01 (@UC0192): ô TÊN (`textForm: "name"`) — luật mô tả chỉ ở lượt MỞ từ câu (`openedByText`); lượt trả lời: chữ người gõ LÀ tên.
      // UX-SMALL-WORDING (V-5 · @UC0197): ô LỜI NHẮN (`textForm: "note"`) bỏ lời đệm cuối câu ("…rồi nha").
      if (slot.textForm === "note") return resolveNoteText(text);
      return slot.textForm === "name" ? resolveNameText(text, { opening: scope.openedByText === true }) : resolveTextSlot(text);
    case "list":
      return resolveListSlot(text, slot.duplicates);
    default: {
      const exhaustive: never = type;
      throw new Error(`Loại ô chưa có resolver: ${String(exhaustive)}`);
    }
  }
}

/**
 * 🔴 AI-INTERP C3 (@UC0201 @UC0161) — ô member TUỲ CHỌN opt-in `unnamedMeansBlank`: chữ KHÔNG khớp ai VÀ chỉ CẢ NHÓM («mọi người», «các phụ huynh còn thiếu»)
 * ⇒ `none/unsaid` (lý do RIÊNG — sec M-2) = người dùng chưa nói gì về ô ⇒ Người làm bỏ ô như nút «Bỏ trống» ⇒ trang dùng nghĩa «bỏ trống» Sổ đã hứa
 * (DEBT-REMIND: nhắc cả đợt) — y như khi Phiếu không gạch gì. Trước: `unmatched` ⇒ hỏi thừa «Nhắc riêng ai?» ở đường chính của trang.
 * Hẹp có chủ đích (re-review):
 *  - CHỈ ô khai `unnamedMeansBlank` (OPT-IN của Sổ — validator: member tuỳ chọn). INC-RECORD.nguoiTaiTro KHÔNG bật: vắng ô ở đó = tài trợ NGOÀI nhóm (HIGH);
 *  - CHỈ lý do `unmatched` — tên khớp 1/≥2 người không bao giờ bị bỏ; ô bắt buộc không vào đây;
 *  - «chỉ cả nhóm» là DANH SÁCH TRẮNG ĐÓNG (`memberTextIsGroupPhrase`, sec M-1) — không `memberTextNamesSomeone`/`dpNameWord` (vị-từ câu chữ): «nó»,
 *    «người đó», tên «Định»/«Sáng», id (kể cả trong ngoặc — L-3) ⇒ vẫn `unmatched` (hỏi), không bao giờ «vắng ô» = phạm vi rộng nhất của trang.
 */
function optionalMemberUnsaid(slot: AiSlot, text: string | null | undefined, res: SlotResolution<string>): SlotResolution<string> {
  // re-review HIGH: OPT-IN theo ô (`unnamedMeansBlank`, validator: chỉ ô member tuỳ chọn) — không ô member tuỳ chọn nào khác (INC-RECORD.nguoiTaiTro).
  if (slot.unnamedMeansBlank !== true || slot.required || res.status === "resolved" || res.reason !== SLOT_RESOLUTION_REASON.UNMATCHED) return res;
  const raw = (text ?? "").trim();
  // sec M-1/L-3: DANH SÁCH TRẮNG — chỉ cụm chỉ CẢ NHÓM mới là vắng ô; tên · đại từ · id (kể cả trong ngoặc) ⇒ giữ `unmatched` (hỏi).
  if (!memberTextIsGroupPhrase(raw)) return res;
  return slotNone(SLOT_RESOLUTION_REASON.UNSAID);
}

/**
 * 🔴 US-AIV2-0109 S11 vòng 2 (@UC0246, QĐ PO 17/09 vế b) — người có ĐÚNG MỘT nhóm không bị hỏi "nhóm nào".
 *
 * Chữ ở ô nhóm không khớp tên nào ⇒ `candidates/unmatched` = CẢ phạm vi. Với một nhóm, đó là câu hỏi chọn-giữa-một: ảnh máy ảo 25/09
 * (lượt SAU đầu tiên của S11) vẫn thấy *"Mình chưa chắc bạn nói đến cái nào"* + một nút "Lớp 5A", vì LLM gạch chân một mẩu câu không
 * phải tên nhóm. Mẩu đó KHÔNG thể là tên một nhóm khác mà actor thuộc — phạm vi chỉ có một — nên tự điền là đúng, và câu trả lời/phiếu
 * vẫn in tên nhóm.
 *
 * Hẹp có chủ đích: CHỈ ô có `autofill`, CHỈ `unmatched` (không `ambiguous`), CHỈ khi phạm vi đúng 1. Ô ĐỢT THU KHÔNG nới:
 * gõ "tháng 10" khi chỉ mở "tháng 9" mà tự chọn là ghi tiền vào sai đợt.
 *
 * 🔴 US-AIV2-0110 S27b (@UC0279 · QĐ PO 27/09) — ô QUỸ nới ĐÚNG MỘT bậc: trang ĐỌC (`pageTier === "read"`). Đo: 12/42 câu bộ chuẩn của
 * READ-DEBTS có span rác CÓ trong câu ở ô `quy` ("nợ", "nhóm", "bạn nào") ⇒ người có một quỹ bị hỏi "chọn giúp" với đúng một nút.
 * Trang ĐỌC không ghi đồng nào và câu trả lời IN tên quỹ (READ-DEBTS "Ai nợ ai trong {tenQuy}", READ-FUND-MONEY "Quỹ {tenQuy}", …), nên
 * người đọc thấy ngay quỹ nào. Trang GHI (T1/T2) GIỮ chốt: gọi tên một quỹ không có mà tự chọn quỹ duy nhất là ghi tiền vào sai quỹ.
 * Cái MẤT PO đã chấp nhận: người CỐ Ý hỏi một quỹ không có trong nhóm thì trang đọc trả về quỹ duy nhất, không báo "không tìm thấy".
 * Vắng `pageTier` ⇒ không nới (fail-closed). MỘT luật cho nhóm và quỹ — cờ `allowed` là điều kiện theo loại ô/bậc trang, không cơ chế thứ hai.
 */
function soleRefWhenUnmatched(slot: AiSlot, res: SlotResolution<string>, refs: readonly NamedRef[] | undefined, allowed: boolean): SlotResolution<string> {
  if (!allowed || slot.autofill === undefined || refs === undefined || refs.length !== 1) return res;
  if (res.status !== "candidates" || res.reason !== SLOT_RESOLUTION_REASON.UNMATCHED) return res;
  return slotResolved(refs[0]!.id, refs[0]!.label);
}
